/**
 * `session.update` builder.
 *
 * Maps the typed {@link VoiceLiveConfig} onto the Realtime-compatible
 * `session.update` client event the adapter sends once the session is
 * established. Over WebRTC this event rides the client's
 * `voice-live-events` data channel; the SDP/signaling control channel stays
 * broker-held. The mapping is intentionally conservative — only the
 * well-known Realtime fields are set, and model mode's `sessionOverrides` is an
 * escape hatch for anything service-specific, applied verbatim last.
 */

import type { VoiceLiveConfig } from "./config.js";

/** A Realtime `session.update` client event. */
export interface SessionUpdateEvent {
  type: "session.update";
  session: Record<string, unknown>;
}

/**
 * Build a `session.update` event from the resolved config. Agent mode omits
 * `instructions` (ignored by the service) and has no `sessionOverrides`.
 */
export function buildSessionUpdate(config: VoiceLiveConfig): SessionUpdateEvent {
  const session: Record<string, unknown> = {};

  if (config.voice !== undefined) {
    session.voice = config.voiceType
      ? { name: config.voice, type: config.voiceType }
      : config.voice;
  }
  if (config.language !== undefined) session.language = config.language;
  if (config.vad !== undefined) session.turn_detection = { type: config.vad };

  if (config.targetType === "model") {
    if (config.instructions !== undefined) session.instructions = config.instructions;
    if (config.sessionOverrides) Object.assign(session, config.sessionOverrides);
  }

  return { type: "session.update", session };
}
